// Shared operation modes for blower, fan, and compressor devices
// Based on ADR-0006 (Operating Mode Configuration Pattern)
//
// Design pattern: Define schema once, export multiple TypeScript types.
// - Makes it clear the modes are intentionally identical
// - Allows future divergence without breaking changes
// - Follows the split flow_volumetric/flow_mass pattern from pumps

import { z } from 'zod';

// --- Individual mode config schemas (shared) ---
//
// The three pressure modes are exported because the pump composes its own union
// from them (it adds `head`, which no other active device has). They must stay a
// single definition: a pump that redeclared them WITHOUT `pressure_type` had the
// flag silently stripped by Zod, which killed the static→total correction on the
// entire desktop-import path. See [[project_active_device_static_pressure_frame]].

export const ActiveDevicePressureDifferentialConfigSchema = z.object({
  mode: z.literal('pressure_differential'),
  value: z.number(),
  unit: z.string(),
  pressure_type: z.enum(['total', 'static']).optional(),
});

export const ActiveDevicePressureSuctionConfigSchema = z.object({
  mode: z.literal('pressure_suction'),
  value: z.number(),
  unit: z.string(),
  pressure_type: z.enum(['total', 'static']).optional(),
});

export const ActiveDevicePressureDischargeConfigSchema = z.object({
  mode: z.literal('pressure_discharge'),
  value: z.number(),
  unit: z.string(),
  pressure_type: z.enum(['total', 'static']).optional(),
});

const ActiveDeviceFlowVolumetricConfigSchema = z.object({
  mode: z.literal('flow_volumetric'),
  value: z.number(),
  unit: z.string(),
});

const ActiveDeviceFlowMassConfigSchema = z.object({
  mode: z.literal('flow_mass'),
  value: z.number(),
  unit: z.string(),
});

const ActiveDeviceFlowTemperatureControlConfigSchema = z.object({
  mode: z.literal('flow_temperature_control'),
  value: z.number(),
  unit: z.string(),
  linked_device_id: z.string().optional(),
});

const ActiveDeviceSpeedConfigSchema = z.object({
  mode: z.literal('specified_speed'),
  value: z.number().positive(),
  unit: z.string(),
});

// Compressor-only mode
const ActiveDevicePressureRatioConfigSchema = z.object({
  mode: z.literal('pressure_ratio'),
  value: z.number().positive(),
  unit: z.string(),
});

// --- Base operation schema (shared by blower and fan) ---

export const ActiveDeviceOperationConfigSchema = z.discriminatedUnion('mode', [
  ActiveDevicePressureDifferentialConfigSchema,
  ActiveDevicePressureSuctionConfigSchema,
  ActiveDevicePressureDischargeConfigSchema,
  ActiveDeviceFlowVolumetricConfigSchema,
  ActiveDeviceFlowMassConfigSchema,
  ActiveDeviceFlowTemperatureControlConfigSchema,
  ActiveDeviceSpeedConfigSchema,
]);

// --- Extended operation schema for compressor (adds pressure_ratio) ---

export const CompressorOperationConfigSchema = z.discriminatedUnion('mode', [
  ActiveDevicePressureDifferentialConfigSchema,
  ActiveDevicePressureSuctionConfigSchema,
  ActiveDevicePressureDischargeConfigSchema,
  ActiveDeviceFlowVolumetricConfigSchema,
  ActiveDeviceFlowMassConfigSchema,
  ActiveDeviceFlowTemperatureControlConfigSchema,
  ActiveDeviceSpeedConfigSchema,
  ActiveDevicePressureRatioConfigSchema,
]);

// --- Device-specific type aliases ---

export type BlowerOperationConfig = z.infer<typeof ActiveDeviceOperationConfigSchema>;
export type FanOperationConfig = z.infer<typeof ActiveDeviceOperationConfigSchema>;
export type CompressorOperationConfig = z.infer<typeof CompressorOperationConfigSchema>;

// Schema aliases for consistency in vertex definitions
export const BlowerOperationConfigSchema = ActiveDeviceOperationConfigSchema;
export const FanOperationConfigSchema = ActiveDeviceOperationConfigSchema;
// CompressorOperationConfigSchema already exported above
